// Words for an application's filing (API docs/filing.md): its steps after
// submission, as staff read them on the case and the country page. Times
// are Beirut wall-clock text ("2026-11-06 09:30"), shown as they are.

import { dayWords } from "src/components/shared/days";

export const KIND_LABELS = {
  form: "Online form",
  appointment: "Appointment",
  fee: "Fee",
  attend: "Go to the appointment",
  submit: "Hand it in",
  passport_back: "Passport back",
  other: "Other",
};

const WALL = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/;

/** "Fri 6 Nov 2026, 09:30" from "2026-11-06 09:30", or "". */
export function wallWords(wall) {
  const m = WALL.exec(String(wall || ""));
  if (!m) return "";
  const date = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  const day = date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).replace(",", "");
  return `${day}, ${m[4]}:${m[5]}`;
}

/** "2026-11-06T09:30" for a datetime-local field, from the API's wall time. */
export const toLocalInput = (wall) => {
  const m = WALL.exec(String(wall || ""));
  return m ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}` : "";
};

/** "$30", "€90", "30 LBP" */
export function moneyWords(amount, currency) {
  if (amount === null || amount === undefined || amount === "") return "";
  try {
    const whole = Number.isInteger(Number(amount));
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount} ${currency || ""}`.trim();
  }
}

/**
 * What a step has recorded, in short lines for the case screen.
 *   travellers: [{ request_id, name, number }]
 */
export function stepDetails(step, travellers = []) {
  const lines = [];
  if (step.kind === "form") {
    for (const t of travellers) {
      const number = step.travellers?.[String(t.request_id)];
      if (number) lines.push(travellers.length > 1 ? `${t.name || `Traveller ${t.number}`}: ${number}` : `Application number ${number}`);
    }
  }
  if (step.kind === "appointment") {
    const when = wallWords(step.scheduled_at);
    lines.push([when ? `${when} (Beirut time)` : "No time yet", step.place].filter(Boolean).join(" · "));
    if (step.reference) lines.push(`Booking ${step.reference}`);
  }
  if (step.kind === "fee") {
    const amount = moneyWords(step.amount, step.currency);
    const who = step.paid_by === "customer" ? "the customer pays" : step.paid_by === "safrati" ? "Safrati pays" : "";
    lines.push([amount && `${amount} per traveller`, step.pay_how, who].filter(Boolean).join(" · ") || "No amount yet");
    if (step.due_at) lines.push(`${step.done ? "Was due" : "Due"} ${wallWords(step.due_at)}`);
    else if (step.due_hours) lines.push(`Due ${step.due_hours} hours after the appointment is booked`);
    if (step.reference) lines.push(`Receipt ${step.reference}`);
  }
  if (step.note) lines.push(`For the customer: ${step.note}`);
  return lines.filter(Boolean);
}

/** The first step not done yet, or null when all are. */
export const nextStepOf = (steps = []) => steps.find((s) => !s.done) || null;

const hostOf = (url) => {
  try {
    return new URL(String(url)).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

/**
 * What a plan's fee step says besides its title (API
 * docs/fees-paid-elsewhere.md), in parts for the steps' summary:
 *   { optional, money: "$30 per traveller, paid to TLScontact Beirut" | "",
 *     source: { label, url } | null, checked: "19 Oct 2026" | "" }
 * `per` "application" is paid once for the whole application; anything
 * else (or nothing) is per traveller, as every fee was before.
 */
export function feeParts(step) {
  if (step?.kind !== "fee") return null;
  const once = step.per === "application";
  const amount = moneyWords(step.amount, step.currency);
  const to = String(step.paid_to || "").trim();
  const head = amount ? `${amount} ${once ? "once per application" : "per traveller"}` : once ? "Once per application" : "";
  const money = [head, to ? `${head ? "paid" : "Paid"} to ${to}` : "", step.paid_by === "safrati" ? "Safrati pays it" : ""]
    .filter(Boolean)
    .join(", ");
  const url = String(step.source_url || "").trim();
  const label = String(step.source_label || "").trim() || hostOf(url);
  const checked = dayWords(step.checked_on);
  return { optional: step.optional === true, money, source: label ? { label, url: /^https?:\/\//i.test(url) ? url : "" } : null, checked };
}

/**
 * A plan's fee step in one line, as staff read it in the steps' summary:
 * "$30 per traveller, paid to TLScontact Beirut · Source: TLScontact Beirut:
 * fees, checked 19 Oct 2026", "Optional extra", or "" for a step that says
 * nothing besides its title.
 */
export function feeSummary(step) {
  const parts = feeParts(step);
  if (!parts) return "";
  const source = parts.source ? `Source: ${parts.source.label}${parts.checked ? `, checked ${parts.checked}` : ""}` : parts.checked ? `Checked ${parts.checked}` : "";
  return [parts.optional ? "Optional extra" : "", parts.money, source].filter(Boolean).join(" · ");
}

/** A plan in one line: "Online form → Appointment → Fee ($30) → …". */
export function planLine(plan) {
  return (plan?.steps || [])
    .map((s) => (s.kind === "fee" && s.amount != null ? `${s.title} (${moneyWords(s.amount, s.currency)})` : s.title))
    .join(" → ");
}
